import React, { lazy, Suspense } from 'react';
import { RouteObject, Navigate } from 'react-router-dom';
import { RoutePermissionGuard } from '@shared/auth/RoutePermissionGuard';
import { Role } from '@shared/auth/AuthContext';

const ChatPage = lazy(() => import('./modules/chat/pages/ChatPage').then(m => ({ default: m.ChatPage })));
const NotificationsPage = lazy(() => import('./modules/broadcasts/pages/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const BroadcastsPage = lazy(() => import('./modules/broadcasts/pages/BroadcastsPage').then(m => ({ default: m.BroadcastsPage })));
const TemplatesPage = lazy(() => import('./modules/broadcasts/pages/TemplatesPage').then(m => ({ default: m.TemplatesPage })));
const PtmPage = lazy(() => import('./modules/ptm/pages/PtmPage').then(m => ({ default: m.PtmPage })));
const CommunityForumPage = lazy(() => import('./modules/forum/pages/CommunityForumPage').then(m => ({ default: m.CommunityForumPage })));

const LoadingFallback: React.FC = () => (
  <div className="flex h-64 items-center justify-center">
    <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const COMM_ROLES: Role[] = ['owner', 'admin', 'branch_head', 'teacher', 'counselor', 'accountant', 'student', 'parent'];
const BROADCAST_ROLES: Role[] = ['owner', 'admin', 'branch_head', 'counselor', 'accountant'];
const PTM_ROLES: Role[] = ['owner', 'admin', 'branch_head', 'teacher', 'counselor', 'parent', 'student'];
const FORUM_ROLES: Role[] = ['owner', 'admin', 'branch_head', 'teacher', 'counselor', 'parent', 'student'];

export const communicationRoutes: RouteObject[] = [
  {
    index: true,
    element: <Navigate to="chat" replace />,
  },
  {
    path: 'chat',
    handle: { crumb: 'In-App Chat Hub' },
    element: (
      <RoutePermissionGuard permission="comm.read" allowedRoles={COMM_ROLES}>
        <Suspense fallback={<LoadingFallback />}>
          <ChatPage />
        </Suspense>
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'notifications',
    handle: { crumb: 'Push Notifications & Alerts' },
    element: (
      <RoutePermissionGuard permission="comm.read" allowedRoles={COMM_ROLES}>
        <Suspense fallback={<LoadingFallback />}>
          <NotificationsPage />
        </Suspense>
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'broadcasts',
    handle: { crumb: 'Broadcast Campaigns' },
    element: (
      <RoutePermissionGuard permission="broadcasts.send" allowedRoles={BROADCAST_ROLES}>
        <Suspense fallback={<LoadingFallback />}>
          <BroadcastsPage />
        </Suspense>
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'templates',
    handle: { crumb: 'Merge Templates' },
    element: (
      <RoutePermissionGuard permission="broadcasts.send" allowedRoles={BROADCAST_ROLES}>
        <Suspense fallback={<LoadingFallback />}>
          <TemplatesPage />
        </Suspense>
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'ptm',
    handle: { crumb: 'PTM Scheduler' },
    element: (
      <RoutePermissionGuard permission="ptm.manage" allowedRoles={PTM_ROLES}>
        <Suspense fallback={<LoadingFallback />}>
          <PtmPage />
        </Suspense>
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'forum',
    handle: { crumb: 'Academic Q&A Forum' },
    element: (
      <RoutePermissionGuard permission="comm.read" allowedRoles={FORUM_ROLES}>
        <Suspense fallback={<LoadingFallback />}>
          <CommunityForumPage />
        </Suspense>
      </RoutePermissionGuard>
    ),
  },
];
